import { useEffect, useRef, useState } from 'react';
import { reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useStore } from '@/app/StoreContext';
import type { RoomSettingsDto } from '@/api/dto';
import { SeatRow } from './SeatRow';
import { RoomSettingsSummary } from './RoomSettingsSummary';
import { InviteBlock } from './InviteBlock';
import { Toast } from '@/components/ui/Toast';
import { Button } from '@/components/ui/Button';
import { runtime } from '@/platform/runtime';
import { applyMainButton, hideMainButton } from '@/platform/mainButton';
import { fx } from '@/platform/feedback';
import styles from './LobbyView.module.css';

function settingsChips(settings: RoomSettingsDto): string[] {
  const chips: string[] = [];
  if (settings.mode === 'Match') {
    chips.push(`Матч · ${settings.totalRounds} раунда`);
  } else if (settings.mode === 'Solo') {
    chips.push('Тренировка');
  } else {
    chips.push('Один раунд');
  }
  chips.push(`${settings.roundDurationSec} сек`);
  if (settings.speedBonus) chips.push('Бонус скорости');
  return chips;
}

export const LobbyView = observer(function LobbyView() {
  const { room, auth } = useStore();
  const isReady = room.me?.isReady ?? false;
  const [joinToast, setJoinToast] = useState<{ name: string; key: number } | null>(null);
  const seenIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    room.room?.players.forEach(p => seenIds.current.add(p.playerId));

    const dispose = reaction(
      () => room.room?.players.map(p => ({ id: p.playerId, name: p.nickname })),
      (players) => {
        if (!players) return;
        for (const p of players) {
          if (!seenIds.current.has(p.id) && p.id !== room.me?.playerId) {
            setJoinToast({ name: p.name, key: Date.now() });
          }
          seenIds.current.add(p.id);
        }
      }
    );
    return dispose;
  }, []);

  useEffect(() => {
    if (!runtime.isMiniApp) return;
    const isSoloEffect = room.room?.settings.mode === 'Solo';
    const enoughPlayers = isSoloEffect || (room.room?.players.length ?? 0) >= 2;
    applyMainButton({
      text: enoughPlayers ? (isSoloEffect ? 'Начать' : isReady ? 'Я не готов' : 'Я готов') : 'Ждём игроков…',
      visible: true,
      active: enoughPlayers,
      onClick: enoughPlayers ? () => {
        fx.tap();
        void room.setReady(!isReady);
      } : undefined,
    });
    return () => hideMainButton();
  }, [isReady, room.room?.players.length, room.room?.settings.mode]);

  if (!room.room) return null;
  const isSolo = room.room.settings.mode === 'Solo';
  const enoughPlayers = isSolo || room.room.players.length >= 2;
  const chips = settingsChips(room.room.settings);

  return (
    <div className={styles.lobby}>
      <div className={styles.scrollArea}>
        <header className={styles.header}>
          <p className={styles.kicker}>{isSolo ? 'Тренировка' : 'Комната создана'}</p>
          <h1 className={styles.title}>
            {isSolo ? 'Моя игра' : (
              <>Комната <span className={styles.codeGradient}>{room.room.code}</span></>
            )}
          </h1>
          <div className={styles.chips}>
            {chips.map(chip => (
              <span key={chip} className={styles.chip}>{chip}</span>
            ))}
          </div>
        </header>

        {!isSolo && (
          <SeatRow
            players={room.room.players}
            hostId={room.room.hostPlayerId ?? ''}
            myId={room.me?.playerId ?? ''}
            myColor={auth.avatarColor}
            maxPlayers={room.room.maxPlayers}
          />
        )}

        {isSolo && <RoomSettingsSummary settings={room.room.settings} />}

        {!isSolo && <InviteBlock code={room.room.code} />}
      </div>

      <div className={styles.ctaWrap}>
        <Button
          variant="primary"
          disabled={!enoughPlayers}
          onClick={() => {
            if (!enoughPlayers) return;
            fx.tap();
            void room.setReady(!isReady);
          }}
          className={styles.ctaBtn}
        >
          {!enoughPlayers ? 'Ждём ещё игроков…' : isSolo ? 'Начать' : isReady ? 'Я не готов' : 'Я готов'}
        </Button>
      </div>

      {joinToast && (
        <Toast
          key={joinToast.key}
          open
          onOpenChange={(open) => { if (!open) setJoinToast(null); }}
          title={`${joinToast.name} в комнате`}
          duration={2400}
        />
      )}
    </div>
  );
});
